/**
 * Copyright 2025 Lumavec Group LLC
 *
 * Team collaboration section - push/pull to team, PR to default branch
 */

import React from 'react';
import { RefreshCw, AlertTriangle, Upload, GitPullRequest, Loader } from 'lucide-react';
import type { RemoteVelocityStatus, SourceControlProvider } from './types';

interface TeamCollaborationSectionProps {
  remoteVelocityStatus: RemoteVelocityStatus | null;
  loadingRemoteStatus: boolean;
  onPushToTeam: () => void;
  pushingToTeam: boolean;
  onPullFromTeam: () => void;
  pullingFromTeam: boolean;
  onShowVelocityPRDialog: () => void;
  isSourceControlConfigured: boolean;
  sourceControlProvider: SourceControlProvider;
  /** Target branch name from organization settings (defaults to 'main') */
  defaultBranch?: string;
}

export function TeamCollaborationSection({
  remoteVelocityStatus,
  loadingRemoteStatus,
  onPushToTeam,
  pushingToTeam,
  onPullFromTeam,
  pullingFromTeam,
  onShowVelocityPRDialog,
  isSourceControlConfigured,
  sourceControlProvider,
  defaultBranch = 'main'
}: TeamCollaborationSectionProps) {
  return (
    <div className="p-4 space-y-2 border-b border-gray-200">
      <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
        Team Collaboration
      </div>
      
      {/* Remote Velocity Status Banner */}
      {remoteVelocityStatus && remoteVelocityStatus.needsPull && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mb-2">
          <div className="flex items-start gap-2">
            <RefreshCw size={14} className="text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-blue-900">
                Team Changes Available
              </p>
              <p className="text-xs text-blue-700 mt-1">
                {remoteVelocityStatus.behindRemote} commit{remoteVelocityStatus.behindRemote !== 1 ? 's' : ''} from team members
              </p>
              <button
                onClick={onPullFromTeam}
                disabled={pullingFromTeam}
                className="mt-2 w-full btn-secondary text-xs py-1.5 flex items-center justify-center gap-1"
              >
                {pullingFromTeam ? (
                  <Loader size={12} className="animate-spin" />
                ) : (
                  <RefreshCw size={12} />
                )}
                Pull from Team
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Remote Conflict Warning */}
      {remoteVelocityStatus?.hasRemoteConflicts && (
        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg mb-2">
          <div className="flex items-start gap-2">
            <AlertTriangle size={14} className="text-yellow-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-yellow-900">
                Potential Conflicts with Team
              </p>
              <p className="text-xs text-yellow-700 mt-1">
                Pull and resolve conflicts before pushing
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Push to Team Button */}
      <button
        onClick={onPushToTeam}
        disabled={pushingToTeam || !isSourceControlConfigured || remoteVelocityStatus?.hasRemoteConflicts}
        className="w-full btn-primary text-sm py-2 flex items-center justify-center gap-1"
        title={!isSourceControlConfigured ? `${sourceControlProvider === 'github' ? 'GitHub' : 'GitLab'} access token required` : 
               remoteVelocityStatus?.hasRemoteConflicts ? 'Resolve conflicts first' : 'Push velocity changes to team'}
      >
        {pushingToTeam ? (
          <Loader size={14} className="animate-spin" />
        ) : (
          <Upload size={14} />
        )}
        Push to Team
        {remoteVelocityStatus?.needsPush && remoteVelocityStatus.aheadOfRemote > 0 && (
          <span className="ml-1 px-1.5 py-0.5 text-xs bg-white/20 rounded">
            {remoteVelocityStatus.aheadOfRemote}
          </span>
        )}
      </button>
      
      {/* Create PR to Default Branch Button */}
      <button
        onClick={onShowVelocityPRDialog}
        disabled={!isSourceControlConfigured}
        className="w-full btn-secondary text-sm py-2 flex items-center justify-center gap-1"
        title={!isSourceControlConfigured ? `${sourceControlProvider === 'github' ? 'GitHub' : 'GitLab'} access token required` : `Create PR from velocity to ${defaultBranch}`}
      >
        <GitPullRequest size={14} />
        Create {sourceControlProvider === 'github' ? 'PR' : 'MR'} to {defaultBranch}
      </button>
      
      {/* Remote Status Indicator */}
      {remoteVelocityStatus && (
        <div className="text-xs text-gray-500 mt-1 flex items-center gap-2">
          {loadingRemoteStatus ? (
            <Loader size={10} className="animate-spin" />
          ) : remoteVelocityStatus.hasRemoteVelocity ? (
            <>
              <span className="w-2 h-2 rounded-full bg-green-500"></span>
              Connected to team
              {(remoteVelocityStatus.aheadOfRemote > 0 || remoteVelocityStatus.behindRemote > 0) && (
                <span className="text-gray-400">
                  ({remoteVelocityStatus.aheadOfRemote}↑ {remoteVelocityStatus.behindRemote}↓)
                </span>
              )}
            </>
          ) : (
            <>
              <span className="w-2 h-2 rounded-full bg-gray-400"></span>
              No team branch yet
            </>
          )}
        </div>
      )}
    </div>
  );
}

